import { css } from '@compiled/react';

import { Accordion, AccordionSize, type AccordionTabProps } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { MOBILE } from '../../utils/constants';
import Heading from '../common/Heading';

/* styles */

const eligibilityContainerStyles = css({
  backgroundColor: '#F8F8F8',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '1440px',
  width: '100%',
  padding: '80px 20px',

  [MOBILE.MEDIA_QUERY]: {
    padding: '48px 20px',
  },
});

const eligibilityTitleStyles = css({
  fontFamily: 'Charlie Display',
  textAlign: 'center',
  marginBottom: '16px',
  fontSize: '40px',
  fontWeight: 700,

  [MOBILE.MEDIA_QUERY]: {
    fontSize: '32px',
  },
});

const eligibilitySubtitleStyles = css({
  textAlign: 'left',
  width: '100%',
  maxWidth: '816px',
  margin: '16px 0',
  marginTop: token('space.1000'),
  fontSize: '32px',
  fontWeight: 600,

  [MOBILE.MEDIA_QUERY]: {
    fontSize: '24px',
    marginTop: '56px',
  },
});

const eligibilityContentStyles = css`
  h4 {
    font-family: 'Charlie Display';
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
  }
  p {
    font-family: 'Inter';
    font-size: 20px;
    line-height: 32px;
    font-weight: 400;
  }
`;

const textCenterStyles = css({
  textAlign: 'center',
});

const eligibilityBodyStyle = css({
  fontSize: '20px',
  lineHeight: '32px',
  maxWidth: '816px',
  margin: '0 auto',
});

const upperTriangleStyle = css({
  maxHeight: '64px',

  [MOBILE.MEDIA_QUERY]: {
    transform: 'translateX(-670px)',
  },
});

const lowerTriangleStyle = css({
  maxHeight: '64px',
  transform: 'rotate(180deg)',
});

const eligibilitySectionStyles = css({
  maxWidth: '1440px', // width of svg
  margin: '0 auto',
});

const accordionContainerStyles = css({
  maxWidth: '816px',
  width: '100%',
});

/* sections */
const S1 = 'Contributing to Atlassian Community Forums';
const S2 = 'Organizing Atlassian Community Events';
const S4 = 'Creating content';

const S1Content = (
  <div css={eligibilityContentStyles}>
    <h4>What they do</h4>
    <p>
      Champions contribute to the Atlassian Community Forums in various ways, including writing articles, generating
      discussions, answering questions, and facilitating groups.
    </p>
    <h4>Who’s eligible</h4>
    <p>
      Before you apply, you must be an active member of Atlassian Community Forums and achieved{' '}
      <a href="https://community.atlassian.com/forums/Announcement-articles/How-to-become-a-Rising-Star/ba-p/2130775">
        Rising Star status
      </a>
      .
    </p>
  </div>
);

const S2Content = (
  <div css={eligibilityContentStyles}>
    <h4>What they do</h4>
    <p>Organizers host, organize, and run Atlassian Community Events, in person or virtually.</p>
    <h4>Who’s eligible</h4>
    <p>Before you apply, you need to:</p>
    <ul>
      <li>Have attended at least one Atlassian Community Event in the past 12 months.</li>
      <li>Commit to host at least one event per quarter (90 days), virtually or in person.</li>
    </ul>
  </div>
);

const S4Content = (
  <div css={eligibilityContentStyles}>
    <h4>What they do</h4>
    <p>
      Content creators publish original thought leadership or learning content around Atlassian products and best
      practices on social media.
    </p>
    <h4>Who’s eligible</h4>
    <p>Before you apply, you must:</p>
    <ul>
      <li>
        Have contributed at least two pieces of original Atlassian-focused thought leadership or learning content on
        social media each month in the last 90 days.
      </li>
    </ul>
  </div>
);

const tabsContent: AccordionTabProps[] = [
  {
    title: S1,
    isExpanded: false,
    children: S1Content,
  },
  {
    title: S2,
    isExpanded: false,
    children: S2Content,
  },
  {
    title: S4,
    isExpanded: false,
    children: S4Content,
  },
];

const triangleDecoration = (
  <svg width="1440" height="64" viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 64H1440V0L0 64Z" fill="#F8F8F8" />
  </svg>
);

export const ChampionsEligibility = () => {
  return (
    <section css={eligibilitySectionStyles}>
      <div css={upperTriangleStyle}>{triangleDecoration}</div>
      <div css={eligibilityContainerStyles}>
        <Heading level={2} css={eligibilityTitleStyles}>
          Who’s eligible to apply?
        </Heading>
        <p css={[eligibilityBodyStyle, textCenterStyles]}>
          There are a few requirements you’ll need to meet to join the program. Learn more to start your champion
          journey today.
        </p>
        <Heading level={3} css={[eligibilityTitleStyles, eligibilitySubtitleStyles]}>
          Success Champions
        </Heading>
        <p css={eligibilityBodyStyle}>
          Currently, the Success Champion track is in Beta. Submit an application to join our waitlist. If you'd like to
          run a company user group before the Success Champion track is generally available, head to our{' '}
          <a href="https://www.atlassian.com/community/company-user-groups">all-in-one user group kit</a>, and join our{' '}
          <a href="https://community.atlassian.com/forums/Company-User-Groups/gh-p/cug">
            Company User Group online group
          </a>
          .
        </p>
        <Heading
          level={3}
          css={[eligibilityTitleStyles, eligibilitySubtitleStyles, { marginBottom: token('space.400') }]}
        >
          Community Champions
        </Heading>
        <div css={accordionContainerStyles}>
          <Accordion
            tabsContent={tabsContent}
            size={AccordionSize.WIDE}
            shouldSingleTabBeOpen={true}
            onChange={() => {}}
          />
        </div>
      </div>
      <div css={lowerTriangleStyle}>{triangleDecoration}</div>
    </section>
  );
};
